import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import { STATUS_META } from "@/lib/learning";

export const StatusBadge = ({ status, className = "" }) => {
  const meta = STATUS_META[status] || STATUS_META.COMING_SOON;
  return (
    <span data-testid={`status-badge-${status?.toLowerCase()}`} className={`inline-block text-[0.58rem] font-semibold uppercase tracking-[0.14em] px-2.5 py-1 ${meta.cls} ${className}`}>
      {meta.label}
    </span>
  );
};

function priceLabel(p) {
  if (p.fee_type === "PAID" && p.packages?.length > 0) {
    const min = Math.min(...p.packages.map((pk) => Number(pk.price) || Infinity));
    return `From ₹${min.toLocaleString("en-IN")}`;
  }
  if (p.fee_type === "PAID" && Number(p.fee) > 0) return `₹${Number(p.fee).toLocaleString("en-IN")}`;
  if (p.fee_type === "FREE") return "Free";
  if (p.fee_type === "DONATION") return "Donation based";
  if (p.fee_type === "SPONSORED") return "Sponsored";
  return "On enquiry";
}

function seatsLabel(p) {
  const editions = p.editions || [];
  const capacity = editions[0]?.capacity || p.capacity || 0;
  if (!capacity) return null;
  const bookableBatches = editions.filter((e) => {
    if (e.registration_status === "CLOSED") return false;
    const daysToStart = e.start_date ? Math.ceil((new Date(e.start_date) - new Date()) / 86400000) : null;
    return daysToStart === null || daysToStart >= 15;
  });
  const seatsLeft = bookableBatches.reduce((sum, e) => sum + Math.max((Number(e.capacity) || 0) - Number(e.seats_booked || 0), 0), 0);
  if (editions.length > 0) return `${seatsLeft} seats left across upcoming batches`;
  return `${capacity} seats per batch`;
}

export const ProgrammeCard = ({ p, index = 0 }) => {
  const seats = seatsLabel(p);
  const membersOnly = p.status === "OFFERED";
  return (
    <Link
      to={`/learning/programme/${p.slug}`}
      data-testid={`programme-card-${p.slug}`}
      className="group flex flex-col border border-ptf-primary/12 bg-white hover:border-ptf-primary/40 transition-colors duration-300"
    >
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <p className="text-[0.6rem] uppercase tracking-[0.2em] text-ptf-text/50">{p.category_name}{p.subcategory && !["LAND", "WATER", "MOUNTAIN", "AIR"].includes(p.subcategory) ? "" : p.subcategory ? ` · ${p.subcategory}` : ""}</p>
          <StatusBadge status={p.status} />
        </div>
        <h3 className="mt-3 font-display uppercase text-xl tracking-tight text-ptf-primary leading-tight group-hover:text-ptf-secondary transition-colors">{p.title}</h3>
        <p data-testid={`programme-card-price-${p.slug}`} className="mt-2 font-display font-bold text-lg text-ptf-primary">{priceLabel(p)}</p>
        <p className="mt-1.5 text-sm text-ptf-text/70 leading-relaxed line-clamp-3 flex-1">{p.description}</p>
        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-[0.62rem] uppercase tracking-[0.12em] text-ptf-text/55 border-t border-ptf-primary/10 pt-3">
          <span>{p.format}</span>
          <span>{p.duration}</span>
          {p.location && p.location !== "To be announced" && <span>{p.location}</span>}
          <span>{p.level}</span>
          {seats && <span data-testid={`programme-card-seats-${p.slug}`}>{seats}</span>}
          {p.certificate === "YES" && <span className="text-ptf-secondary font-semibold">Certificate</span>}
          {p.certificate === "DEPENDS" && <span>Certificate: depends on edition</span>}
        </div>
        {membersOnly && (
          <p data-testid={`programme-card-membership-${p.slug}`} className="mt-3 flex items-center gap-1.5 text-[0.6rem] uppercase tracking-[0.12em] text-ptf-secondary">
            <ShieldCheck className="h-3.5 w-3.5" /> Foundation membership required to book
          </p>
        )}
        <span className="mt-4 inline-flex items-center gap-1 text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-ptf-primary group-hover:text-ptf-secondary transition-colors">
          View Programme <ArrowUpRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </Link>
  );
};
